import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import useNotification from '@design-system/Notification/useNotification';
import useUserStore from 'hooks/useUserStore';

import { connectApi } from 'utils/api/baas/axiosBase';

/**
 * Order type interface for API response
 */
interface OrderType {
  uuid: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * API response interface for order types
 */
interface OrderTypesResponse {
  data: OrderType[];
  pagination: {
    count: number;
    page: number;
    pageSize: number;
    next: string | null;
    previous: string | null;
  };
}

/**
 * Hook return interface
 */
interface UseOrderTypesReturn {
  orderTypes: OrderType[];
  orderTypesState: {
    error: boolean;
    fetched: boolean;
    fetching: boolean;
  };
  refetchOrderTypes: () => void;
}

/**
 * Fetches order types for the current store from API
 *
 * @param {string} storeId - Store UUID
 * @returns {Promise<OrderTypesResponse>} Order types response
 */
const getOrderTypesRequest = async (storeId: string): Promise<OrderTypesResponse> => {
  const response = await connectApi.get(`/v1/stores/${storeId}/order-types/?page=1&page_size=100`);
  return response.data;
};

/**
 * Custom hook for fetching order types for a store
 * Provides order types data with loading states and error handling
 * Only fetches data when in store mode (storeId is available)
 *
 * @returns {UseOrderTypesReturn} Order types data and state
 * @returns {OrderType[]} returns.orderTypes - Array of order types
 * @returns {Object} returns.orderTypesState - Loading and error states
 * @returns {boolean} returns.orderTypesState.error - Whether there was an error
 * @returns {boolean} returns.orderTypesState.fetched - Whether data has been fetched
 * @returns {boolean} returns.orderTypesState.fetching - Whether currently fetching
 * @returns {Function} returns.refetchOrderTypes - Function to refetch data
 *
 * @example
 * const { orderTypes, orderTypesState, refetchOrderTypes } = useOrderTypes();
 *
 * if (orderTypesState.fetching) {
 *   return <LoadingSpinner />;
 * }
 *
 * return (
 *   <Autocomplete
 *     options={orderTypes}
 *     getOptionLabel={(option) => option.name}
 *   />
 * );
 */
export const useOrderTypes = (): UseOrderTypesReturn => {
  const { t } = useTranslation('menuMaker');
  const { setNotification } = useNotification();
  const { storeId } = useUserStore();

  const { data, isError, isFetched, isFetching, refetch } = useQuery(
    ['order-types', storeId],
    async () => {
      if (!storeId) {
        throw new Error('Store ID is required');
      }
      return getOrderTypesRequest(storeId);
    },
    {
      enabled: !!storeId, // Only fetch when storeId is available (store mode)
      retry: false,
      select: (response) => response.data || [],
      onError: () => {
        setNotification(t('menuMaker:orderTypes.fetchError'), {
          variant: 'error',
        });
      },
    },
  );

  const orderTypesState = {
    error: isError,
    fetched: isFetched,
    fetching: isFetching,
  };
  return {
    orderTypes: data || [],
    orderTypesState,
    refetchOrderTypes: refetch,
  };
};
